/**
 * What the Hub inbox says when there is nothing to list, and what it offers.
 *
 * `page` replaces both sections; `waiting` and `answered` sit inside their own
 * card. Actions are ids the page maps to a handler, listed only when this
 * viewer can carry them out.
 */

const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const policyholders = (n) => count(n, 'policyholder', 'policyholders');

const state = (key, headline, body, actions = []) => ({ key, headline, body, actions });

const scopeLabel = (brokerFilter, brokerName) => {
  if (!brokerFilter) return '';
  if (brokerFilter === 'none') return ' among policyholders with no broker';

  return ` in ${brokerName || 'this broker'}'s threads`;
};

const noMatch = ({ search, brokerFilter, brokerName }) => {
  const term = search ? ` for “${search}”` : '';
  const label = search ? 'Clear the search' : 'Clear the filter';

  return state(
    'no-match',
    'No conversations match',
    `Nothing${term}${scopeLabel(brokerFilter, brokerName)}.`,
    [{ id: 'clear', label }],
  );
};

const emptyHubBody = ({ hubName, invitationsPending, brokerCount }) => {
  if (invitationsPending > 0) {
    return `${count(invitationsPending, 'person has', 'people have')} been invited to ${hubName}. They appear here once they register, and their messages land in this inbox.`;
  }

  if (brokerCount > 0) {
    return `Messages appear here once policyholders join ${hubName}. Invite your first policyholders — the broker assigned to each one sees their messages first.`;
  }

  return `Messages appear here once policyholders join ${hubName}. Invite your first policyholders, or add the brokers who will look after them — whichever comes first.`;
};

const emptyHub = (input) => {
  const actions = [{ id: 'invite', label: input.invitationsPending > 0 ? 'Invite more policyholders' : 'Invite policyholders', primary: true }];

  if (input.brokerCount === 0 && input.canAddTeam) actions.push({ id: 'team', label: 'Add your team' });

  return state('empty-hub', 'Nobody in the hub yet', emptyHubBody(input), actions);
};

const noneAssigned = () => state(
  'none-assigned',
  'No policyholders assigned to you yet',
  "When somebody is assigned to you, their conversation opens here and you'll see it before anyone else. Nothing to do in the meantime — the owner assigns policyholders to brokers.",
  [{ id: 'sessions', label: "See what's on this week" }],
);

const noConversations = ({ isBroker, bookSize }) => {
  if (isBroker) {
    return state('no-conversations', 'No conversations yet', `You look after ${policyholders(bookSize)}. When one of them writes, it appears here.`);
  }

  return state(
    'no-conversations',
    'No conversations yet',
    `${policyholders(bookSize)} can reach you here. Nothing has been sent yet — messages land in this inbox and the broker assigned to each person sees them first.`,
    [{ id: 'invite', label: 'Invite more policyholders' }],
  );
};

const caughtUp = () => state('caught-up', 'All caught up', 'Nobody is waiting on a reply.');

const nothingAnswered = () => state('nothing-answered', 'Nothing answered yet', 'Replies you send appear here.');

const noBookState = (input) => {
  if (input.bookSize > 0) return null;

  return input.isBroker ? noneAssigned() : emptyHub(input);
};

const pageState = (input, filtered) => {
  if (input.waiting + input.answered > 0) return null;
  if (filtered) return noMatch(input);

  return noBookState(input);
};

const waitingState = (input, filtered) => {
  if (input.waiting > 0) return null;
  if (!filtered && input.answered === 0) return noConversations(input);

  return caughtUp();
};

/**
 * @param {object} input
 * @param {boolean} input.isBroker      a broker confined to their own book
 * @param {number} input.waiting        rows in Waiting
 * @param {number} input.answered       rows not waiting
 * @param {number|null} input.bookSize rows the server says this viewer looks after; null until known
 * @param {string} [input.search]
 * @param {string} [input.brokerFilter] '' · 'none' · a broker id; ignored for a broker
 * @param {string} [input.brokerName]
 * @param {string} [input.hubName]
 * @param {number} [input.invitationsPending]
 * @param {number|null} [input.brokerCount] null until known
 * @param {boolean} [input.canAddTeam]
 */
export function inboxEmptyStates(input) {
  const filtered = Boolean(input.search) || (!input.isBroker && Boolean(input.brokerFilter));
  const page = pageState(input, filtered);

  if (page) return { page, waiting: null, answered: null };

  return {
    page: null,
    waiting: waitingState(input, filtered),
    answered: input.answered > 0 || input.isBroker ? null : nothingAnswered(),
  };
}
